import React from "react";
import { Stashlink } from "../../types";
import { formatCurrencyToUsd } from "../../utils/format";
import clsx from "clsx";
interface HeadToHeadResultProps {
  player1Stashlink?: Stashlink;
  player2Stashlink?: Stashlink;
  player1Score?: number;
  player2Score?: number;
}

const HeadToHeadResult: React.FC<HeadToHeadResultProps> = ({ player1Stashlink, player2Stashlink, player1Score, player2Score }) => {
  const player1 = player1Stashlink?.user;
  const player2 = player2Stashlink?.user;

  const isPlayer1Winner = (player1Score ?? 0) >= (player2Score ?? 0);
  const charity1Logo = player1Stashlink?.campaign.charity?.imageUrl;
  const charity2Logo = player2Stashlink?.campaign.charity?.imageUrl;

  const WinnerBadge = () => (
    <div className="bg-black/40 text-primary-l1 px-2.5 py-1.5 font-primary rounded-full font-extrabold text-xl">Winner</div>
  );
  const RunnerUpBadge = () => (
    <div className="bg-black/40 text-light-grey font-primary text-xs px-2.5 py-1.5 rounded-full font-bold">2nd</div>
  );

  return (
    <div className="relative w-full flex flex-col items-center">
      <div className="mb-3 text-center">
        <div className="text-sm font-base-white font-secondary font-medium">A total of</div>
        <div className="text-primary-l1 text-[22px] mb-1 font-extrabold leading-[120%] font-primary">
          {formatCurrencyToUsd((player1Score ?? 0) + (player2Score ?? 0))} Raised
        </div>
      </div>

      <section className="rounded-lg p-6 relative items-center flex w-full justify-center max-w-md">
        <div className="flex justify-center space-x-4 relative w-full h-[400px]">
          <div
            className={clsx(
              "absolute left-0",
              isPlayer1Winner
                ? "rounded-[12px_64px_12px_12px] w-[55%] border-t border-primary-l1 border-l border-r-[6px] border-b-[6px]"
                : "rounded-[12px_64px_12px_12px] w-[45%] border-t border-black border-l border-r-[6px] border-b-[6px]"
            )}>
            <div className="relative">
              <img
                src={player1?.profilePhoto}
                alt={player1?.fullName}
                className={clsx(
                  "rounded-tl-[12px] rounded-tr-[64px] mb-2 w-full object-cover",
                  isPlayer1Winner ? "h-[226px]" : "h-[186px]"
                )}
              />
              <div className="max-w-4/5 absolute text-[17px] text-shadow-custom font-primary font-bold text-base-white leading-[120%] bottom-1 left-4">
                {player1?.fullName}
              </div>
            </div>
            <div className="relative text-left pt-4 pl-4 pb-0">
              <p className="text-primary-l1 text-lg font-extrabold font-secondary">
                {formatCurrencyToUsd(player1Stashlink?.campaign.currentAmount)}
              </p>
              <p className="text-sm text-grey font-secondary mb-3">raised for</p>
              <img
                src={charity1Logo}
                alt="charity-logo"
                className="object-cover size-[60px] rounded-full mb-2 border border-1 border-black"
              />
              <p className="text-base text-base-white font-medium w-5/6">{player1Stashlink?.campaign?.charity?.name}</p>
              <a href={player1Stashlink?.campaign?.charity?.website} target="_blank" rel="noopener noreferrer">
                <p className="text-base text-light-grey underline font-secondary mb-4">Learn more</p>
              </a>
            </div>
            <div className="absolute top-2 left-2">{isPlayer1Winner ? <WinnerBadge /> : <RunnerUpBadge />}</div>
          </div>

          <div
            className={clsx(
              "absolute right-0",
              isPlayer1Winner
                ? "rounded-[64px_12px_12px_12px] w-[45%] border-t border-black border-r-[6px] border-l border-b-[6px]"
                : "rounded-[64px_12px_12px_12px] w-[55%] border-t border-primary-l1 border-r-[6px] border-l border-b-[6px]"
            )}>
            <div className="relative">
              <img
                src={player2?.profilePhoto}
                alt={player2?.fullName}
                className={clsx(
                  "rounded-tl-[64px] rounded-tr-[12px] mb-2 w-full object-cover",
                  isPlayer1Winner ? "h-[186px]" : "h-[226px]"
                )}
              />
              <div className="max-w-4/5 absolute text-[17px] text-shadow-custom font-primary font-bold text-base-white leading-[120%] bottom-1 right-4">
                {player2?.fullName}
              </div>
            </div>
            <div className="relative text-right flex items-end flex-col pt-4 pr-4 pb-0">
              <p className="text-primary-l1 text-lg font-extrabold font-secondary">
                {formatCurrencyToUsd(player2Stashlink?.campaign.currentAmount)}
              </p>
              <p className="text-sm text-grey font-secondary mb-3">raised for</p>
              <img
                src={charity2Logo}
                alt="charity-logo"
                className="object-cover size-[60px] rounded-full mb-2 border border-1 border-black"
              />
              <p className="text-base text-base-white font-medium w-5/6">{player2Stashlink?.campaign?.charity?.name}</p>
              <a href={player2Stashlink?.campaign?.charity?.website} target="_blank" rel="noopener noreferrer">
                <p className="text-base text-light-grey underline font-secondary mb-4">Learn more</p>
              </a>
            </div>
            <div className="absolute top-2 right-2">{isPlayer1Winner ? <RunnerUpBadge /> : <WinnerBadge />}</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HeadToHeadResult;
